import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Issues (or rotates) the MCP API key that belongs to a paying AI Access member.
 *
 * The caller is resolved from their verified bearer token, and a key is only
 * issued when the database says their $25/month subscription is live. The
 * plaintext key is returned exactly once — only its SHA-256 hash is stored, so
 * it can never be read back later, only rotated.
 */
export const issueMcpKey = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    const { data: active, error: entitlementErr } = await supabase.rpc("has_active_ai_access", {
      _user_id: userId,
    });
    if (entitlementErr) throw new Error("Could not confirm your AI Access subscription.");
    if (active !== true) {
      throw new Error("An active AI Access subscription is required to issue an MCP key.");
    }

    const bytes = crypto.getRandomValues(new Uint8Array(32));
    const secret = `sk_statlign_${
      Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("")
    }`;
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(secret));
    const keyHash = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Rotation: retire every previous key for this member first, so exactly one
    // live credential exists per account.
    await supabaseAdmin
      .from("mcp_api_keys")
      .update({ active: false })
      .eq("owner_id", userId)
      .eq("source", "ai_subscription");

    const { data: row, error } = await supabaseAdmin
      .from("mcp_api_keys")
      .insert({
        label: `AI Access — ${userId.slice(0, 8)}`,
        key_hash: keyHash,
        active: true,
        owner_id: userId,
        source: "ai_subscription",
        created_by: userId,
      })
      .select("id, label, rate_limit_per_min, created_at")
      .single();

    if (error) {
      console.error("Failed to issue MCP key", error);
      throw new Error("Could not issue your key. Please try again.");
    }

    return { key: secret, key_info: row };
  });
